import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface RewardDetails {
  merchantName: string;
  cardName: string;
  termCTA: string;
  rewardsType: string;
  signupBonus: {
    amount: string;
    amountSubtext: string;
    title: string;
    items: { text: string };
  };
  rewards: [
    {
      amount: string;
      amountSubtext: string;
      title: string;
      items: { text: string };
    },
    {
      amount: string;
      amountSubtext: string;
      title: string;
      items: { text: string };
    },
  ];
  rewardsBonus: Array<{
    amount: string;
    amountSubtext: string;
    title: string;
    items: { text: string };
    type: string;
    offerConfigUUIDs?: string[];
  }>;
  rewardsNote: string;
  primaryValueProp: string;
}

export interface Color {
  dark: string;
  light: string;
  contrastDark?: string;
  contrastLight?: string;
}

export interface DisplayAmount {
  displaySymbol: string;
  displayValue: string;
  displayUnit?: string;
  exponent: number;
  valueInMinUnit: number;
  negative: boolean;
}

export const getEmptyAmount = () => {
  return {
    displaySymbol: '',
    displayValue: '',
    displayUnit: '',
    exponent: 0,
    negative: false,
    valueInMinUnit: 0,
  };
};

export interface CardDesignOption {
  cardDesignID: string;
  title: string;
  fee?: DisplayAmount;
  reason?: string;
  status: string;
}

export enum CardDesignStatus {
  SELECTABLE = 'SELECTABLE',
  NOT_SELECTABLE = 'NOT_SELECTABLE',
  HIDDEN = 'HIDDEN',
}

interface CardDesignOptionImage {
  cardDesignID: string;
  physical: string;
  render: string;
}

interface CardDesignOptionImages {
  [key: string]: CardDesignOptionImage;
}

interface CounterOffer {
  title: string;
  titleHighlight: string;
  subtitle: string;
  image: string;
  lightModeColor: string;
  darkModeColor: string;
  hyperlinkButtonName: string;
  url: string;
  disclosure: string;
}

export interface MerchantData {
  merchantName: string;
  productUUID: string;
  cardImg: string;
  appUiImg: string;
  webUiImg: string;
  cardOfferImg: string;
  cardBackImg: string;
  cardDesignOptions: CardDesignOption[];
  cardDesignOptionsImages: CardDesignOptionImages;
  counterOffer: CounterOffer;
  rewardDetails: RewardDetails;
  color: Color;
  logoIcon: string;
  deeplink: string;
  successQR: string;
  errorQR: string;
  checkApplicationQR: string;
  confirmLaterQR: string;
  manualReviewQR: string;
  lockupImage: string;
  downloadAppQR: string;
}

const initialState: MerchantData = {
  merchantName: '',
  productUUID: '',
  cardImg: '',
  appUiImg: '',
  webUiImg: '',
  cardOfferImg: '',
  cardBackImg: '',
  cardDesignOptions: [],
  cardDesignOptionsImages: {},
  counterOffer: {
    title: '',
    titleHighlight: '',
    subtitle: '',
    image: '',
    lightModeColor: '',
    darkModeColor: '',
    hyperlinkButtonName: '',
    url: '',
    disclosure: '',
  },
  rewardDetails: {
    merchantName: '',
    cardName: '',
    termCTA: '',
    rewardsType: '',
    signupBonus: {
      amount: '',
      amountSubtext: '',
      title: '',
      items: { text: '' },
    },
    rewards: [
      { amount: '', amountSubtext: '', title: '', items: { text: '' } },
      { amount: '', amountSubtext: '', title: '', items: { text: '' } },
    ],
    rewardsBonus: [],
    rewardsNote: '',
    primaryValueProp: '',
  },
  color: { dark: '', light: '' },
  logoIcon: '',
  deeplink: '',
  successQR: '',
  errorQR: '',
  checkApplicationQR: '',
  confirmLaterQR: '',
  manualReviewQR: '',
  lockupImage: '',
  downloadAppQR: '',
};

const merchantDataSlice = createSlice({
  name: 'merchantData',
  initialState,
  reducers: {
    setProductUUID: (state, action: PayloadAction<string>) => {
      state.productUUID = action.payload;
    },
    setCardImg: (state, action: PayloadAction<string>) => {
      state.cardImg = action.payload;
    },
    setCardDesignOptionImages: (
      state,
      action: PayloadAction<CardDesignOptionImage>
    ) => {
      const { cardDesignID, physical, render } = action.payload;
      state.cardDesignOptionsImages[cardDesignID] = {
        cardDesignID,
        physical,
        render,
      };
    },
    setAppUiImg: (state, action: PayloadAction<string>) => {
      state.appUiImg = action.payload;
    },
    setWebUiImg: (state, action: PayloadAction<string>) => {
      state.webUiImg = action.payload;
    },
    setCardOfferImg: (state, action: PayloadAction<string>) => {
      state.cardOfferImg = action.payload;
    },
    setCardBackImg: (state, action: PayloadAction<string>) => {
      state.cardBackImg = action.payload;
    },
    setRewardDetails: (state, action: PayloadAction<RewardDetails>) => {
      state.rewardDetails = action.payload;
    },
    setColor: (state, action: PayloadAction<Color>) => {
      state.color = action.payload;
    },
    setLogoIcon: (state, action: PayloadAction<string>) => {
      state.logoIcon = action.payload;
    },
    setupMerchantAndEnv: (state, action: PayloadAction<string>) => {
      state.productUUID = action.payload;
    },
    setMerchantName: (state, action: PayloadAction<string>) => {
      state.merchantName = action.payload;
    },
    setDeeplink: (state, action: PayloadAction<string>) => {
      state.deeplink = action.payload;
    },
    setSuccessQR: (state, action: PayloadAction<string>) => {
      state.successQR = action.payload;
    },
    setErrorQR: (state, action: PayloadAction<string>) => {
      state.errorQR = action.payload;
    },
    setCheckApplicationQR: (state, action: PayloadAction<string>) => {
      state.checkApplicationQR = action.payload;
    },
    setConfirmLaterQR: (state, action: PayloadAction<string>) => {
      state.confirmLaterQR = action.payload;
    },
    setManualReviewQR: (state, action: PayloadAction<string>) => {
      state.manualReviewQR = action.payload;
    },
    setDownloadAppQR: (state, action: PayloadAction<string>) => {
      state.downloadAppQR = action.payload;
    },
    setCounterOffer: (state, action: PayloadAction<CounterOffer>) => {
      state.counterOffer = action.payload;
    },
    resetMerchantData: (_state) => initialState,
    setLockupImage: (state, action: PayloadAction<string>) => {
      state.lockupImage = action.payload;
    },
    setRewardsNote: (state, action: PayloadAction<string>) => {
      state.rewardDetails.rewardsNote = action.payload;
    },
  },
});

export const {
  setProductUUID,
  setCardImg,
  setAppUiImg,
  setWebUiImg,
  setCardOfferImg,
  setCardBackImg,
  setCardDesignOptionImages,
  setRewardDetails,
  setColor,
  setLogoIcon,
  setupMerchantAndEnv,
  setMerchantName,
  setDeeplink,
  setSuccessQR,
  setErrorQR,
  setCheckApplicationQR,
  setConfirmLaterQR,
  setManualReviewQR,
  setDownloadAppQR,
  setCounterOffer,
  resetMerchantData,
  setLockupImage,
} = merchantDataSlice.actions;
export default merchantDataSlice.reducer;
